import { settingsIcon } from "@/components/icons/SVGIcons";
import ProfileClient from "@/components/shellComponents/Sidebar/Profile";
import { Button } from "@/components/UI/Button";
import Modal from "@/components/UI/Modal";
import { initialPricesConfigsFromAPI } from "@/store/pricesTabStoreACTIONS";
import useToolbarStore from "@/store/toolbarOpenStore";
import { initialWorkspaceTabsFromAPI } from "@/store/workspacesTabsStoreACTIONS";
import { useAuth0 } from "@auth0/auth0-react";
import { useTheme } from "next-themes";
import React, { useEffect, useState } from "react";
import AddWidgetButton from "./AddWidgetButton";
import CompanyLogoAndName from "./CompanyLogoAndName";
import MoreOptionsButton from "./MoreOptionsButton";
import SidebarDivider from "./SidebarDivider";
import ToolbarButton from "./ToolbarButton";
import WorkspaceButtonContainer from "./WorkspaceButtonContainer";
import HelpdeskButton from "./HelpdeskButton";
import HelpdeskForm from "./HelpdeskForm";
import workspacesTabsStore from "@/store/workspacesTabsStore";
import useModalStore from "@/store/modalStore";
import NotificationCenter from "./NotificationCenter";

type TSidebar = {
  firstLoad: boolean;
  setFirstLoad: (_value: boolean) => void;
};

const Sidebar: React.FC<TSidebar> = ({ firstLoad, setFirstLoad }) => {
  const toolbarOpen = useToolbarStore((state) => state.open);

  const { getAccessTokenSilently } = useAuth0();

  const { theme } = useTheme();
  let isStrictModeUseEffectFirstMount = false;
  useEffect(() => {
    async function getWorkspaceTabs() {
      // Logic form ACTIONS file
      // eslint-disable-next-line react-hooks/exhaustive-deps
      isStrictModeUseEffectFirstMount = !isStrictModeUseEffectFirstMount; //
      await initialWorkspaceTabsFromAPI(
        getAccessTokenSilently,
        isStrictModeUseEffectFirstMount,
      );
      // Also send a request for all prices configs
      initialPricesConfigsFromAPI(getAccessTokenSilently);
      setFirstLoad(true);
    }
    getWorkspaceTabs(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { user, isAuthenticated, logout } = useAuth0();
  const [modalOpen, setModalOpen] = useState(false);
  const [modalToLoad, setModalToLoad] = useState("");
  const [helpdeskFormOpen, setHelpdeskFormOpen] = useState(false);

  const shouldOpenSettings = useModalStore(
    (state) => state.shouldOpenSettings,
  );
  const clearOpenSettingsRequest = useModalStore(
    (state) => state.clearOpenSettingsRequest,
  );

  useEffect(() => {
    if (shouldOpenSettings) {
      setModalToLoad("Settings");
      setModalOpen(true);
      clearOpenSettingsRequest();
    }
  }, [shouldOpenSettings, clearOpenSettingsRequest]);

  const tabNamesAndActive = workspacesTabsStore(
    (state) => state.tabNamesAndActive,
  );

  return (
    <div
      className={`flex h-[100%] items-start bg-cubeBase-900 dark:bg-cubeBaseDark-900`}
    >
      <div className={`SidebarShell  ml-3 h-[99%] pr-1`}>
        <div
          id="toolbarLargeContainerOpen"
          className={` flex h-full flex-col items-center border-r border-gray-300 bg-cubeBase-800  dark:border-black dark:bg-cubeBaseDark-800 ${toolbarOpen ? "w-[180px]" : "w-[50px]"} rounded-xl`}
        >
          <div className="SidebarInnerContainer h-full w-full rounded p-2 pt-[10px]">
            <div className="flex h-full w-full flex-col justify-between rounded ">
              <div>
                <div className="">
                  <CompanyLogoAndName />
                </div>
                <SidebarDivider />
                <div
                  className={`toggleSidebarContainer flex ${toolbarOpen ? "w-full flex-row" : "flex-col"}`}
                >
                  <ToolbarButton toolbarOpen={toolbarOpen} />
                  {toolbarOpen ? <SidebarDivider isHorizontal={false} /> : null}
                  <AddWidgetButton toolbarOpen={toolbarOpen} theme={theme} />
                </div>
                <SidebarDivider />
                {tabNamesAndActive.length > 0 && (
                  <div id="logedInWorkspaceButtonsContainer">
                    <WorkspaceButtonContainer
                      toolbarOpen={toolbarOpen}
                      theme={theme}
                      getAccessTokenSilently={getAccessTokenSilently}
                    />
                    <MoreOptionsButton
                      toolbarOpen={toolbarOpen}
                      theme={theme}
                      getAccessTokenSilently={getAccessTokenSilently}
                      firstLoad={firstLoad}
                    />
                    <SidebarDivider />
                  </div>
                )}
                <HelpdeskButton
                  toolbarOpen={toolbarOpen}
                  onClick={() => setHelpdeskFormOpen(true)}
                />
              </div>
              <div>
                {/* Below for the additional settings, notificaitons etc.  */}
                <div
                  className={`flex items-center ${toolbarOpen ? "w-full flex-row justify-start gap-0" : "flex-col  gap-2"} `}
                >
                  {toolbarOpen ? (
                    <ProfileClient theme={theme} user={user} logout={logout} /> // Toolbar in Code twice to switch oreder it's displayed.
                  ) : null}
                  {isAuthenticated ? (
                    <>
                      {/* Notification Center Icon */}
                      <NotificationCenter theme={theme} />
                      {/* Settings Icon */}
                      <div className="flex w-[35px] items-center justify-center">
                        <Button
                          variant="icon"
                          onClick={() => {
                            setModalToLoad("Settings");
                            setModalOpen(true);
                          }}
                          id="SettingIconButtonNavbar"
                        >
                          {settingsIcon}
                        </Button>
                      </div>
                    </>
                  ) : null}
                  {toolbarOpen ? null : (
                    <ProfileClient theme={theme} user={user} logout={logout} /> // Toolbar in Code twice to switch oreder it's displayed.
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {modalOpen ? (
        <Modal
          setModalOpen={setModalOpen}
          toLoad={modalToLoad}
          closeOnClick={true}
          heading={modalToLoad}
          modalClassName="h-[90%] w-[90%] min-[1400px]:h-[75%] min-[1900px]:w-[75%]"
          id={modalToLoad}
        ></Modal>
      ) : null}
      {helpdeskFormOpen ? (
        <HelpdeskForm onClose={() => setHelpdeskFormOpen(false)} />
      ) : null}
    </div>
  );
};

export default Sidebar;
